import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useNotifications } from '@toolpad/core';
import { FormContainer, PasswordElement, useForm } from 'react-hook-form-mui';
import * as Yup from 'yup';
import {
    Button,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    Stack,
} from '@mui/material';

import { EditButtons } from '@barboxx/admin-components';
import { useAuth } from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import { User } from '@barboxx/models';
import { useBool } from '@barboxx/react-utils';

interface Props {
    user: User;
}

export const USER_PASSWORD_SCHEMA: Yup.ObjectSchema<User.UpdatePasswordRequest> =
    Yup.object().shape({
        password: Yup.string()
            .min(8, 'Too Short!')
            .max(50, 'Too Long!')
            .required(),
        newPassword: Yup.string()
            .min(8, 'Too Short!')
            .max(50, 'Too Long!')
            .required(),
    });

export const UserPasswordCard = ({ user }: Props) => {
    const [editable, editableActions] = useBool();
    const notifications = useNotifications();
    const { logout } = useAuth();

    const formContext = useForm<User.UpdatePasswordRequest>({
        resolver: yupResolver(USER_PASSWORD_SCHEMA),
    });

    const mutateUpdateUser = useMutation({
        mutationKey: ['updateUserPassword', user?.id],
        mutationFn: (update: User.UpdatePasswordRequest) =>
            Api.Users.updatePassword(update),
        onSuccess: logout,
    });

    const mutateInvalidateTokens = useMutation({
        mutationKey: ['customerInvalidateTokens'],
        mutationFn: Api.Users.invalidateTokens,
        onSuccess: logout,
    });

    const onSubmit = (values: User.UpdatePasswordRequest) =>
        mutateUpdateUser
            .mutateAsync(values)
            .then(() => {
                notifications.show('Password Updated!', {
                    severity: 'success',
                });

                editableActions.off();
                formContext.reset();
            })
            .catch(e => {
                notifications.show('Error Updating Password!', {
                    severity: 'error',
                });
            });

    const handleCancel = () => {
        editableActions.off();
        formContext.reset(undefined);
    };

    const invalidateTokens = async () => mutateInvalidateTokens.mutateAsync();

    return (
        <FormContainer onSuccess={onSubmit} formContext={formContext}>
            <Card>
                <CardHeader title="Update User Password" />
                <CardContent>
                    <Stack
                        gap={2}
                        direction="column"
                        justifyContent="space-between"
                    >
                        <Stack gap={2} direction="row" sx={{ flex: 1 }}>
                            <PasswordElement
                                name="password"
                                label="Current Password"
                                required
                                fullWidth
                                disabled={!editable}
                            />

                            <PasswordElement
                                name="newPassword"
                                label="New Password"
                                required
                                fullWidth
                                disabled={!editable}
                            />
                        </Stack>

                        <EditButtons
                            formContext={formContext}
                            handleCancel={handleCancel}
                            editable={editable}
                            editableActions={editableActions}
                        />
                    </Stack>
                </CardContent>
                <CardActions>
                    <Button
                        onClick={invalidateTokens}
                        loading={mutateInvalidateTokens.isPending}
                    >
                        Invalidate Tokens
                    </Button>
                </CardActions>
            </Card>
        </FormContainer>
    );
};
